import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, Ghost, LogOut } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Accueil", end: true },
  { to: "/boutique", label: "Boutique" },
  { to: "/boutique?cat=Spécial Enfants", label: "Spécial Enfants" },
  { to: "/boutique?cat=Déguisements", label: "Déguisements" },
  { to: "/boutique?cat=Décorations", label: "Décorations" },
  { to: "/boutique?cat=Accessoires", label: "Accessoires" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count, setDrawerOpen } = useCart();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md bg-[#08060B]/85 border-b border-amber-900/30"
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <span className="relative">
              <Ghost className="h-7 w-7 sm:h-8 sm:w-8 text-amber-500 group-hover:text-amber-400 transition-colors" />
            </span>
            <div className="leading-none">
              <span className="font-display text-lg sm:text-2xl font-extrabold tracking-wide text-stone-100">
                Croque-Mitaine
              </span>
              <span className="hidden sm:block eyebrow mt-0.5">Boutique d'Halloween</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.label}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wide transition-colors hover:text-amber-400 ${
                    isActive ? "text-amber-500" : "text-stone-300"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            {user && user.email ? (
              <div className="hidden sm:flex items-center gap-2">
                {user.role === "admin" && (
                  <button
                    onClick={() => navigate("/admin")}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-amber-400 hover:bg-amber-500/10 transition-colors text-sm font-semibold"
                    data-testid="admin-nav-button"
                  >
                    Admin
                  </button>
                )}
                <button
                  onClick={() => navigate("/compte")}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-stone-300 hover:text-amber-400 hover:bg-white/5 transition-colors text-sm font-semibold"
                  data-testid="account-button"
                >
                  <User className="h-4 w-4" />
                  <span className="max-w-[110px] truncate">{user.name}</span>
                </button>
                <button
                  onClick={logout}
                  className="p-2 rounded-lg text-stone-400 hover:text-red-400 hover:bg-white/5 transition-colors"
                  data-testid="logout-button"
                  title="Se déconnecter"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => navigate("/connexion")}
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg text-stone-300 hover:text-amber-400 hover:bg-white/5 transition-colors text-sm font-semibold"
                data-testid="login-nav-button"
              >
                <User className="h-4 w-4" /> Connexion
              </button>
            )}

            <button
              onClick={() => setDrawerOpen(true)}
              className="relative p-2.5 rounded-lg text-stone-200 hover:text-amber-400 hover:bg-white/5 transition-colors"
              data-testid="cart-button"
              aria-label="Panier"
            >
              <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />
              {count > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-amber-500 text-[#08060B] text-xs font-extrabold flex items-center justify-center font-mono-tech"
                  data-testid="cart-count-badge"
                >
                  {count}
                </span>
              )}
            </button>

            <button
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden p-2.5 rounded-lg text-stone-200 hover:bg-white/5"
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-amber-900/30 bg-[#08060B]/95 px-4 py-4" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.label}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg font-semibold ${isActive ? "text-amber-500 bg-white/5" : "text-stone-300"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="h-px bg-amber-900/30 my-2" />
            {user && user.email ? (
              <>
                <button onClick={() => { setOpen(false); navigate("/compte"); }} className="px-3 py-3 text-left font-semibold text-stone-300 rounded-lg">
                  Mon compte
                </button>
                <button onClick={() => { setOpen(false); logout(); }} className="px-3 py-3 text-left font-semibold text-red-400 rounded-lg">
                  Se déconnecter
                </button>
              </>
            ) : (
              <button onClick={() => { setOpen(false); navigate("/connexion"); }} className="px-3 py-3 text-left font-semibold text-stone-300 rounded-lg">
                Connexion / Inscription
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
